import type { MaybeRefOrGetter, Ref } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { readonly, ref, shallowRef, toValue, watch } from 'vue';
import { AUTO_RETRY_GRACE_MS, AUTO_RETRY_RECOVERY_MS } from '#layers/base/constants/auto-retry';
import { getRetryDelay } from '#layers/base/utils/retry-delay';
import { isTransientError } from '#layers/base/utils/transient-error';

export interface UseAutoRetryOptions {
	/** The failure to react to. Falsy means nothing is wrong. */
	error: MaybeRefOrGetter<unknown>;
	/** Truthy while a request is in flight; an outcome is only judged once it goes falsy. */
	loading: MaybeRefOrGetter<boolean>;
	/**
	 * Re-issues the failed request. Must be safe to send twice: a transient failure can be reported
	 * after the request was already applied, so only wire this to a read.
	 */
	retry: () => void;
}

export interface UseAutoRetryReturn {
	/**
	 * The failure to show the user, or null while it is still hidden behind retries.
	 *
	 * Owning the displayed error instead of gating the source one keeps the churn of a load cycle —
	 * a failure assigned, then cleared by the next attempt — away from whatever renders it, and holds
	 * the message up while a retry is in flight and the source error is momentarily null.
	 */
	error: Readonly<Ref<unknown>>;
	/** True while a failure is hidden behind a scheduled or in-flight retry. */
	isRetrying: Readonly<Ref<boolean>>;
	/** Cancels the pending retry and starts over, displayed error included. For explicit user intent. */
	reset: () => void;
}

/**
 * Retries a failing request until it succeeds, hiding the failure for the first
 * `AUTO_RETRY_GRACE_MS` so the ones that resolve themselves — a dropped connection, a fetch timeout,
 * an engine restarting into 502 — never become something to click through.
 *
 * Past the grace the failure is shown, but the retries carry on behind it with a doubling delay, so
 * an engine that comes back an hour later still brings the page back on its own. They stop only when
 * the request recovers or when `reset()` is called.
 */
export function useAutoRetry(options: UseAutoRetryOptions): UseAutoRetryReturn {
	const { error, loading, retry } = options;

	const displayedError = ref<unknown>(null);
	const isRetrying = ref(false);

	let attempts = 0;

	/** True once the grace elapsed and the failure reached the user. Retries continue regardless. */
	let hasSurfaced = false;

	/** Error the pending retry is for. Re-reporting it must not schedule a second retry. */
	let pendingError: unknown;

	/** Latest transient failure: the grace can elapse mid-retry, when the source error is null. */
	let lastTransientError: unknown;

	// The delay doubles with every attempt, so it is a ref the timer reads at `start()`.
	const retryDelayMs = shallowRef(0);

	/** Waits out the delay, then re-issues the request. */
	const { start: startRetryTimer, stop: cancelRetryTimer } = useTimeoutFn(() => {
		pendingError = undefined;
		retry();
	}, retryDelayMs, { immediate: false });

	/** Shows the failure once it has been hidden for `AUTO_RETRY_GRACE_MS`. */
	const { start: startGrace, stop: cancelGrace } = useTimeoutFn(surface, AUTO_RETRY_GRACE_MS, { immediate: false });

	/** Resets the backoff once the request has been healthy for `AUTO_RETRY_RECOVERY_MS`. */
	const { start: startRecovery, stop: cancelRecovery } = useTimeoutFn(resetRetryState, AUTO_RETRY_RECOVERY_MS, { immediate: false });

	/** Hands the failure to the user. The retry in flight and the ones after it are left alone. */
	function surface() {
		// The grace elapsed while the query sat settled and healthy: the transient failure resolved
		// itself, so there is nothing to show. Clear the retry state so the next outage is hidden by its
		// own fresh grace instead of inheriting this elapsed one.
		if (!toValue(loading) && !toValue(error)) {
			resetRetryState();
			return;
		}

		hasSurfaced = true;

		// Error first: a consumer reading both must never catch a moment where nothing is wrong and
		// nothing is pending while the failure is in fact unresolved.
		displayedError.value = lastTransientError;
		isRetrying.value = false;
	}

	/** Clears the retry bookkeeping, leaving the displayed error alone. */
	function resetRetryState() {
		attempts = 0;
		hasSurfaced = false;
		pendingError = undefined;
		lastTransientError = undefined;

		cancelRetryTimer();
		cancelGrace();
		cancelRecovery();

		isRetrying.value = false;
	}

	/** Starts over, so the next transient failure gets the full grace again. */
	function reset() {
		resetRetryState();

		displayedError.value = null;
	}

	function scheduleRetry(currentError: unknown) {
		// A load cycle can report the same failure twice — the main page reloading in front of the
		// failing one does that — which would cancel the pending retry and restart its delay.
		if (currentError === pendingError) return;

		// One grace for the whole outage, not one per attempt.
		if (attempts === 0) startGrace();

		attempts++;
		pendingError = currentError;

		if (!hasSurfaced) isRetrying.value = true;

		retryDelayMs.value = getRetryDelay(attempts);
		startRetryTimer();
	}

	// Default `pre` flush on purpose: rstore drops its in-flight count before assigning the error,
	// so a synchronous watcher would catch the gap between the two and read it as a success.
	watch(
		[() => toValue(error), () => toValue(loading)],
		([currentError, isLoading]) => {
			if (isLoading) {
				cancelRecovery();
				return;
			}

			if (!currentError) {
				isRetrying.value = false;
				displayedError.value = null;

				// Being healthy right now proves little: a refresh reloads the main page before the
				// failing one, so every attempt passes through a moment where nothing is wrong. Only a
				// quiet spell with nothing in flight counts as recovery, otherwise the backoff would
				// reset on every pass and the retries would hammer the engine at the base delay.
				if (attempts > 0) startRecovery();

				return;
			}

			cancelRecovery();

			if (!isTransientError(currentError)) {
				resetRetryState();
				displayedError.value = currentError;

				return;
			}

			lastTransientError = currentError;

			// Past the grace the user is watching the failure, so keep it the current one.
			if (hasSurfaced) displayedError.value = currentError;

			scheduleRetry(currentError);
		},
		{ immediate: true },
	);

	// No scope cleanup of our own: `useTimeoutFn` stops itself when this scope goes away.
	return { error: readonly(displayedError), isRetrying: readonly(isRetrying), reset };
}
